import {Component, NgZone, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {Subject, fromEvent} from 'rxjs';
import {debounceTime, takeUntil} from 'rxjs/operators';
import {AiChatUser} from '../models/ai-chat-user.model';
import {AiChatUserService} from '../services/ai-chat-user.service';
import {AiChatAccessService} from '../services/ai-chat-access.service';
import {AiChatAttachmentsService} from '../services/ai-chat-attachments.service';
import {AiChatSessionStateStore} from '../state/ai-chat-session-state.store';
import {AgentFormService} from '../services/agent-form.service';
import {AgentFormsTool, AgentFormFieldOptionsTool, AgentPrepareFormsTool, AgentCurrentFormTool, AgentUpdateFormTool} from '../cyn/fck-chat-module/core/tools/agent-forms.tool';
import { ChatController } from '../cyn/chat-module/core/chat.controller';
import { AgentAppPagesTool } from '../cyn/fck-chat-module/core/tools/agent-app-pages.tool';
import { AgentExcelExportTool } from '../cyn/fck-chat-module/core/tools/agent-excel-export.tool';
import { AgentPageScreenshotTool } from '../cyn/fck-chat-module/core/tools/agent-page-screenshot.tool';
import { AgentRegistryFiltersTool } from '../cyn/fck-chat-module/core/tools/agent-registry-filters.tool';
import { AgentExcelFileService } from '../cyn/fck-chat-module/services/agent-excel-file.service';
import { AgentManager } from '../cyn/fck-chat-module/core/managers/agent.manager';
import { AiChatSessionRenameService } from '../cyn/fck-chat-module/services/ai-chat-session-rename.service';
import { AiChatAlertService } from '../cyn/fck-chat-module/services/ai-chat-alert.service';
import { normalizeHistoryQuery } from '../cyn/fck-chat-module/core/utils/chat-pagination';
import {
  AiChatResizeCorner,
  captureAnchoredPosition,
  clampWindowPosition,
  hasAnchorInsets,
  positionFromInsets,
  resizeFromCorner,
} from './ai-chat-window-layout';

const AI_CHAT_WS_PATH = '/core/ai-chat/ws';
const WIDGET_WIDTH = 400;
const WIDGET_HEIGHT = 600;
const MIN_WIDGET_WIDTH = 400;
const MAX_WIDGET_WIDTH = 900;
const MIN_WIDGET_HEIGHT = 600;
const MAX_WIDGET_HEIGHT = 900;
const DEFAULT_INSET_X = 16;
const DEFAULT_INSET_Y = 16;
// Старое стартовое положение (до переноса кнопки в хедер) — мигрируем на новое.
const LEGACY_DEFAULT_INSET_X = 20;
const LEGACY_DEFAULT_INSET_Y = 90;
const WIDGET_SIZE_LIMITS = {
  minWidth: MIN_WIDGET_WIDTH,
  maxWidth: MAX_WIDGET_WIDTH,
  minHeight: MIN_WIDGET_HEIGHT,
  maxHeight: MAX_WIDGET_HEIGHT,
};
const HEADER_BUTTON_ID = 'ai-chat-header-button';

@Component({
  selector: 'app-ai-chat',
  templateUrl: './ai-chat.component.html',
  styleUrls: ['./ai-chat.component.scss'],
  encapsulation: ViewEncapsulation.None,
  host: {
    'ng-non-bindable': '',
  },
})
export class AiChatComponent implements OnInit, OnDestroy {
  widgetWidth = WIDGET_WIDTH;
  widgetHeight = WIDGET_HEIGHT;

  public chatController?: ChatController;

  x = 0;
  y = 0;
  enabled = false;
  isOpen = false;
  isWidgetVisible = false;
  shouldAnimateOpen = false;
  shouldAnimateClose = false;
  user: AiChatUser | null = null;
  restoreScrollTop: number | null = null;

  private dragging = false;
  private resizing = false;
  private resizeCorner: AiChatResizeCorner = 'se';
  private dragOffsetX = 0;
  private dragOffsetY = 0;
  private resizeStartX = 0;
  private resizeStartY = 0;
  private resizeStartWidth = WIDGET_WIDTH;
  private resizeStartHeight = WIDGET_HEIGHT;
  private resizeStartLeft = 0;
  private resizeStartTop = 0;
  private headerButtonObserver?: MutationObserver;
  private headerButtonSyncScheduled = false;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly stateStore: AiChatSessionStateStore,
    private readonly accessService: AiChatAccessService,
    private readonly attachmentsService: AiChatAttachmentsService,
    private readonly userService: AiChatUserService,
    private readonly sessionRenameService: AiChatSessionRenameService,
    private readonly alertService: AiChatAlertService,
    private readonly excelFileService: AgentExcelFileService,
    private readonly zone: NgZone,
    private readonly forms: AgentFormService,
  ) {}

  ngOnInit(): void {
    fromEvent(window, 'resize')
      .pipe(debounceTime(150), takeUntil(this.destroy$))
      .subscribe(() => this.onBrowserResize());

    // Кнопка живёт в хедере, который рендерят разные фронтенды (spa/spa2/ractive)
    // и часто позже веб-компонента. Поэтому слушаем клики делегированием и
    // следим за появлением кнопки в DOM, а не за порядком инициализации.
    this.zone.runOutsideAngular(() => {
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onDocumentKeydown);
      this.observeHeaderButton();
    });

    this.accessService
      .ensureAccess()
      .pipe(takeUntil(this.destroy$))
      .subscribe((access) => {
        this.enabled = access.enabled;
        this.syncHeaderButton();

        if (!access.enabled) {
          return;
        }

        this.loadAcceptedAttachmentTypes();
        this.restoreSizeOrReset();
        this.applyAnchoredPosition(true);
        this.restoreOpenState();
        this.loadUser();
        this.syncHeaderButton();
      });
  }

  private readonly onDocumentClick = (event: MouseEvent): void => {
    const target = event.target as Element | null;
    if (target && target.closest(`#${HEADER_BUTTON_ID}`)) {
      this.onHeaderButtonActivate();
    }
  };

  private readonly onDocumentKeydown = (event: KeyboardEvent): void => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    const target = event.target as Element | null;
    if (target && target.closest(`#${HEADER_BUTTON_ID}`)) {
      event.preventDefault();
      this.onHeaderButtonActivate();
    }
  };

  private onHeaderButtonActivate(): void {
    if (!this.enabled) {
      return;
    }

    // Слушатели висят вне зоны Angular — возвращаемся в неё, чтобы обновить виджет.
    this.zone.run(() => {
      this.toggleChat();
      // toggleChat мог быть проигнорирован во время анимации закрытия.
      this.syncHeaderButton();
    });
  }

  /** Хедер может перерисоваться в любой момент (Angular/AngularJS), поэтому следим за DOM. */
  private observeHeaderButton(): void {
    if (typeof MutationObserver === 'undefined') {
      return;
    }

    // Вне зоны Angular: мутаций в DOM много, а синхронизация кнопки не влияет на вид чата.
    this.zone.runOutsideAngular(() => {
      this.headerButtonObserver = new MutationObserver(() => this.scheduleHeaderButtonSync());
      this.headerButtonObserver.observe(document.documentElement, {
        childList: true,
        subtree: true,
      });
    });
  }

  private scheduleHeaderButtonSync(): void {
    if (this.headerButtonSyncScheduled) {
      return;
    }

    this.headerButtonSyncScheduled = true;
    requestAnimationFrame(() => {
      this.headerButtonSyncScheduled = false;
      this.syncHeaderButton();
    });
  }

  private syncHeaderButton(): void {
    const button = document.getElementById(HEADER_BUTTON_ID);
    if (!button) {
      return;
    }

    const isChatOpen = this.enabled && this.isOpen;

    button.style.display = this.enabled ? '' : 'none';
    button.setAttribute('data-ai-chat-open', isChatOpen ? 'true' : 'false');
  }

  private loadAcceptedAttachmentTypes(): void {
    this.attachmentsService
      .getAcceptedTypes()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (attachmentsAcceptedTypes) => {
          this.stateStore.patchState({attachmentsAcceptedTypes});
        },
      });
  }

  private initializeChat(): void {
    if (this.chatController) {
      return;
    }

    const tools = [
        new AgentPageScreenshotTool(this.attachmentsService),
        new AgentRegistryFiltersTool(),
        new AgentAppPagesTool(),
        new AgentExcelExportTool(this.excelFileService),
        new AgentFormsTool(this.forms),
        new AgentFormFieldOptionsTool(this.forms),
        new AgentPrepareFormsTool(this.forms),
        new AgentCurrentFormTool(this.forms),
        new AgentUpdateFormTool(this.forms),
    ];

    const agentManager = new AgentManager(
      this.getProxyWsUrl(),
      this.stateStore.getOrCreateTabId(),
      tools,
      this.sessionRenameService,
      this.attachmentsService,
      this.alertService,
      this.stateStore,
    );
    this.chatController = new ChatController([agentManager], agentManager);
    this.restoreActiveSession();
  }

  private getProxyWsUrl(): string {
    const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    return `${wsProtocol}://${window.location.host}${AI_CHAT_WS_PATH}`;
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onDocumentKeydown);
    this.headerButtonObserver?.disconnect();
    this.headerButtonObserver = undefined;
    this.destroy$.next();
    this.destroy$.complete();
    this.saveSize();
    this.savePosition();
    this.stopDragging();
    this.stopResizing();
    this.chatController?.destroy();
    this.forms.stop();
  }

  private loadUser(): void {
    this.userService
      .ensureUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        this.user = user;
        if (user) this.forms.start();
      });
  }

  onDragStart(event: MouseEvent): void {
    if (this.resizing) {
      return;
    }
    event.preventDefault();
    this.dragging = true;
    this.dragOffsetX = event.clientX - this.x;
    this.dragOffsetY = event.clientY - this.y;
    document.addEventListener('mousemove', this.onDragMove);
    document.addEventListener('mouseup', this.onDragEnd);
  }

  onResizeStart(event: MouseEvent, corner: AiChatResizeCorner): void {
    event.preventDefault();
    event.stopPropagation();
    this.resizing = true;
    this.resizeCorner = corner;
    this.resizeStartX = event.clientX;
    this.resizeStartY = event.clientY;
    this.resizeStartWidth = this.widgetWidth;
    this.resizeStartHeight = this.widgetHeight;
    this.resizeStartLeft = this.x;
    this.resizeStartTop = this.y;
    document.addEventListener('mousemove', this.onResizeMove);
    document.addEventListener('mouseup', this.onResizeEnd);
  }

  toggleChat(): void {
    if (this.shouldAnimateClose) {
      return;
    }

    if (this.isOpen) {
      this.isOpen = false;
      this.shouldAnimateOpen = false;
      this.shouldAnimateClose = true;
      this.saveOpenState();
      this.syncHeaderButton();
      return;
    }

    this.initializeChat();
    this.isOpen = true;
    this.isWidgetVisible = true;
    this.shouldAnimateClose = false;
    this.shouldAnimateOpen = true;
    this.applyAnchoredPosition(false);
    this.saveOpenState();
    this.syncHeaderButton();
  }

  private onDragMove = (event: MouseEvent): void => {
    if (!this.dragging) {
      return;
    }
    this.x = event.clientX - this.dragOffsetX;
    this.y = event.clientY - this.dragOffsetY;
  };

  private onDragEnd = (): void => {
    this.savePosition();
    this.stopDragging();
  };

  private onResizeMove = (event: MouseEvent): void => {
    if (!this.resizing) {
      return;
    }

    const next = resizeFromCorner(
      {
        x: this.resizeStartLeft,
        y: this.resizeStartTop,
        width: this.resizeStartWidth,
        height: this.resizeStartHeight,
      },
      {
        x: event.clientX - this.resizeStartX,
        y: event.clientY - this.resizeStartY,
      },
      this.resizeCorner,
      WIDGET_SIZE_LIMITS,
    );
    const clamped = clampWindowPosition(next.x, next.y, next, this.getViewportSize());

    this.widgetWidth = next.width;
    this.widgetHeight = next.height;
    this.x = clamped.x;
    this.y = clamped.y;
  };

  private onResizeEnd = (): void => {
    this.saveSize();
    this.savePosition();
    this.stopResizing();
  };

  private stopDragging(): void {
    this.dragging = false;
    document.removeEventListener('mousemove', this.onDragMove);
    document.removeEventListener('mouseup', this.onDragEnd);
  }

  private stopResizing(): void {
    this.resizing = false;
    this.resizeCorner = 'se';
    document.removeEventListener('mousemove', this.onResizeMove);
    document.removeEventListener('mouseup', this.onResizeEnd);
  }

  private getViewportSize(): {width: number; height: number} {
    return {
      width: window.innerWidth,
      height: window.innerHeight,
    };
  }

  private getWidgetRect(): {x: number; y: number; width: number; height: number} {
    return {
      x: this.x,
      y: this.y,
      width: this.widgetWidth,
      height: this.widgetHeight,
    };
  }

  private resetPosition(): void {
    const viewport = this.getViewportSize();
    const size = {width: this.widgetWidth, height: this.widgetHeight};
    const next = positionFromInsets(
      {insetX: DEFAULT_INSET_X, insetY: DEFAULT_INSET_Y},
      size,
      viewport,
      'bottom-right',
    );
    const clamped = clampWindowPosition(next.x, next.y, size, viewport);
    this.x = clamped.x;
    this.y = clamped.y;
  }

  private onBrowserResize(): void {
    if (this.dragging || this.resizing) {
      return;
    }

    this.applyAnchoredPosition(false);
  }

  private applyAnchoredPosition(persistMigration: boolean): void {
    const viewport = this.getViewportSize();
    const size = {width: this.widgetWidth, height: this.widgetHeight};
    const savedPosition = this.stateStore.getState()?.window?.position;

    if (hasAnchorInsets(savedPosition)) {
      const isLegacyDefault =
        savedPosition.anchor === 'bottom-right'
        && savedPosition.insetX === LEGACY_DEFAULT_INSET_X
        && savedPosition.insetY === LEGACY_DEFAULT_INSET_Y;
      const insets = isLegacyDefault
        ? {insetX: DEFAULT_INSET_X, insetY: DEFAULT_INSET_Y}
        : savedPosition;
      const next = positionFromInsets(insets, size, viewport, savedPosition.anchor);
      const clamped = clampWindowPosition(next.x, next.y, size, viewport);
      this.x = clamped.x;
      this.y = clamped.y;
      if (isLegacyDefault) {
        this.savePosition();
      }
      return;
    }

    if (savedPosition && Number.isFinite(savedPosition.x) && Number.isFinite(savedPosition.y)) {
      const clamped = clampWindowPosition(savedPosition.x, savedPosition.y, size, viewport);
      this.x = clamped.x;
      this.y = clamped.y;
      this.savePosition();
      return;
    }

    this.resetPosition();
    if (persistMigration) {
      this.savePosition();
    }
  }

  private restoreSizeOrReset(): void {
    const savedSize = this.stateStore.getState()?.window?.size;
    if (!savedSize || !Number.isFinite(savedSize.width) || !Number.isFinite(savedSize.height)) {
      this.widgetWidth = WIDGET_WIDTH;
      this.widgetHeight = WIDGET_HEIGHT;
      return;
    }

    this.widgetWidth = Math.min(MAX_WIDGET_WIDTH, Math.max(MIN_WIDGET_WIDTH, savedSize.width));
    this.widgetHeight = Math.min(MAX_WIDGET_HEIGHT, Math.max(MIN_WIDGET_HEIGHT, savedSize.height));
  }

  private restoreOpenState(): void {
    const savedIsOpen = this.stateStore.getState()?.ui?.isOpen;
    this.isOpen = Boolean(savedIsOpen);
    this.isWidgetVisible = this.isOpen;
    this.shouldAnimateOpen = false;
    this.shouldAnimateClose = false;
    if (this.isOpen) {
      this.initializeChat();
    }
  }

  onWidgetAnimationEnd(): void {
    if (this.shouldAnimateClose) {
      this.shouldAnimateClose = false;
      this.isWidgetVisible = false;
      return;
    }

    this.shouldAnimateOpen = false;
    window.dispatchEvent(new Event('resize'));
  }

  private savePosition(): void {
    const position = captureAnchoredPosition(this.getWidgetRect(), this.getViewportSize());
    this.x = position.x;
    this.y = position.y;
    this.stateStore.patchState({
      window: {
        position,
      },
    });
  }

  private saveSize(): void {
    this.stateStore.patchState({
      window: {
        size: {
          width: this.widgetWidth,
          height: this.widgetHeight,
        },
      },
    });
  }

  private saveOpenState(): void {
    this.stateStore.patchState({
      ui: {
        isOpen: this.isOpen,
      },
    });
  }

  onActiveSessionChange(sessionId: string | null): void {
    this.stateStore.patchState({
      ui: {
        activeSessionId: sessionId,
        ...(sessionId ? {} : { messagesScrollTop: null, messagesPage: null }),
      },
    });

    if (!sessionId) {
      this.restoreScrollTop = null;
    }
  }

  onScrollPositionChange(scrollTop: number): void {
    this.stateStore.patchState({
      ui: {
        messagesScrollTop: scrollTop,
      },
    });
  }

  private restoreActiveSession(): void {
    const state = this.stateStore.getState()?.ui;
    const savedSessionId = state?.activeSessionId;
    if (!savedSessionId || !this.chatController) {
      return;
    }

    const savedScrollTop = state?.messagesScrollTop;
    const messagesPage = state?.messagesPage;
    this.restoreScrollTop =
      typeof savedScrollTop === 'number' && Number.isFinite(savedScrollTop)
        ? savedScrollTop
        : null;

    this.chatController.managers[0]?.openSession(
      savedSessionId,
      messagesPage?.sessionId === savedSessionId ? normalizeHistoryQuery(messagesPage) : {},
    );
  }
}
